import { FaroButtonSpinner } from "@components/common/button/faro-button-spinner";
import { SphereButtonProps } from "@components/common/sphere-button";
import { SphereTooltip } from "@components/common/sphere-tooltip";
import { blue, neutral, NO_TRANSLATE_CLASS } from "@flat-ui";
import { Button, ButtonProps, useTheme } from "@mui/material";
import { MouseEventHandler, PropsWithChildren, ReactNode } from "react";
import { Link } from "react-router-dom";
import {
  ColorProp,
  getRippleColorWithOpacity,
} from "@utils/ripple-color-utils";

interface Props
  extends Pick<
    SphereButtonProps,
    | "to"
    | "target"
    | "isDisabled"
    | "isLoading"
    | "loadingIndicatorColor"
    | "loadingTrackColor"
    | "sx"
    | "dataTestId"
    | "shouldNotTranslate"
  >,
  Pick<ButtonProps, "size" | "startIcon" | "endIcon"> {
  /** Callback event after clicking the button */
  onClick: MouseEventHandler;

  /** Text to show as tooltip */
  tooltipText?: ReactNode;

  /**
   * True when the button should only show the icon and no text attached.
   * It will then make sure the icon is properly centered in the button, otherwise
   * there is a small padding between the icon and the rest of the content.
   */
  hasOnlyIcon?: boolean;

  /** Whether to hide hovering effect */
  shouldHideHoveringEffect?: boolean;

  /** Whether to hide focusing effect */
  shouldHideFocusingEffect?: boolean;

  /**
   * Optional background color to apply to the ripple effect on hover and on pressed.
   * This must not contain the opacity value otherwise the ripple color will be static.
   * For ripple, opacity is applied internally with the value 15%.
   * If not provided, defaults to neutral[200] behavior.
   */
  rippleColor?: ColorProp;
}

/** Renders text button with custom default color */
export function FaroTextButton({
  onClick,
  isDisabled,
  tooltipText,
  children,
  startIcon,
  endIcon,
  hasOnlyIcon,
  size,
  sx = {},
  to,
  target,
  dataTestId,
  shouldHideHoveringEffect = false,
  shouldHideFocusingEffect = false,
  shouldNotTranslate = false,
  isLoading = false,
  loadingIndicatorColor = neutral[300],
  loadingTrackColor = neutral[0],
  rippleColor,
}: PropsWithChildren<Props>): JSX.Element {
  const theme = useTheme();

  // Calculate ripple color with opacity
  const rippleColorWithOpacity = getRippleColorWithOpacity(rippleColor, theme);

  // Even if the button is inside a Box with className={NO_TRANSLATE_CLASS}, that's not enough to disable translation
  // of the tooltip. So this is needed:
  const tooltipTitle =
    tooltipText && shouldNotTranslate ? (
      <span className={NO_TRANSLATE_CLASS}>{tooltipText}</span>
    ) : (
      tooltipText
    );

  return (
    <SphereTooltip title={tooltipTitle}>
      <Button
        disabled={isDisabled || isLoading}
        variant="text"
        onClick={onClick}
        disableFocusRipple={true}
        disableRipple={true}
        disableElevation={true}
        disableTouchRipple={true}
        draggable={false}
        LinkComponent={to ? Link : undefined}
        // We need to use this spread operator to avoid passing the `to` prop to the button,
        // because there is a type error in the ButtonProps type.
        {...(to ? { to, target } : {})}
        startIcon={startIcon}
        endIcon={endIcon}
        data-testid={dataTestId}
        size={size || "medium"}
        sx={{
          fontSize: "12px",
          textTransform: "none",
          color: blue[500],
          "&:hover": {
            backgroundColor: shouldHideHoveringEffect
              ? "transparent"
              : rippleColorWithOpacity ?? neutral[200],
          },
          "&:focus-visible": {
            backgroundColor: shouldHideFocusingEffect
              ? "transparent"
              : rippleColorWithOpacity ?? neutral[200],
          },
          ...(rippleColor && {
            "&& .MuiTouchRipple-child": {
              backgroundColor: `${rippleColor}3F`,
            },
          }),
          "& .MuiButton-startIcon": {
            margin: hasOnlyIcon ? "0px" : undefined,
          },
          ...sx,
        }}
      >
        {shouldNotTranslate ? (
          <span className={NO_TRANSLATE_CLASS}>{children}</span>
        ) : (
          children
        )}
        <FaroButtonSpinner
          shouldHide={!isLoading}
          loadingIndicatorColor={loadingIndicatorColor}
          loadingTrackColor={loadingTrackColor}
        />
      </Button>
    </SphereTooltip>
  );
}
